import React, { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/css';
import SelectField from './SelectField';
import Button from '../general/Button';
import ConfirmationDialog from '../structure/ConfirmationDialog';
import { color_shades_lighter, color_shades_dark, color_colors_ocean, color_text_light, color_variants_ocean_light_1 } from '../../constants/colors';
import { TimeRange, DaySchedule as SharedDaySchedule, convert12HourTo24Hour, convert24HourTo12Hour } from '../../utils/weeklyScheduleFormatters';

const addIcon = require('../../content/images/add-circle.svg');
const deleteIcon = require('../../content/images/trash.svg');

type DayStatus = 'unset' | 'open' | 'closed';

// Extends the shared DaySchedule with a 3-state status so 'Closed' vs 'unset' can be distinguished
interface DaySchedule extends SharedDaySchedule {
    status: DayStatus;
}

type BulkActionKind = 'copy' | 'close-weekends';

interface PendingBulkAction {
    kind: BulkActionKind;
    title: string;
    message: string;
    sourceDay?: string;
    targetDays: string[];
}

interface ToastState {
    message: string;
    type: 'success' | 'warning';
}

interface TimeRangeFieldProps {
    name?: string;
    label?: string;
    value?: string; // JSON string of DaySchedule[]
    onChange?: (value: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    className?: string;
    error?: string;
    defaultValue?: string;
    onReset?: () => void;
}

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WEEKDAY_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKEND_DAYS = ['Saturday', 'Sunday'];

const getDayShortLabel = (day: string): string => {
    switch (day) {
        case 'Monday': return 'Mon';
        case 'Tuesday': return 'Tue';
        case 'Wednesday': return 'Wed';
        case 'Thursday': return 'Thu';
        case 'Friday': return 'Fri';
        case 'Saturday': return 'Sat';
        case 'Sunday': return 'Sun';
        default: return day;
    }
};

// Modern select styles
const modernSelectStyle = css`
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    width: 100%;
    min-width: 140px;
    margin-bottom: 0;
    
    /* Override label container height */
    .css-og1717-labelcontainer {
        height: 0;
        display: none;
    }
    
    .Select-control {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        min-height: 44px;
        background: white;
        transition: all 0.2s ease;
        
        &:hover {
            border-color: ${color_colors_ocean};
        }
    }
    
    &.is-focused:not(.is-open) .Select-control {
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
    }
    
    .Select-placeholder,
    .Select-value {
        padding: 11px 14px;
        font-size: 14px;
        line-height: 1.4;
        display: flex;
        align-items: center;
    }
    
    .Select-value-label {
        color: ${color_text_light};
    }
    
    .Select-input {
        padding: 11px 14px;
        height: 44px;
    }
    
    .Select-input > input {
        padding: 0;
        line-height: 1.4;
        font-size: 14px;
        color: ${color_text_light};
    }
    
    .Select-menu-outer {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        margin-top: 4px;
        z-index: 1000;
    }
    
    .Select-menu {
        max-height: 240px;
        border-radius: 6px;
    }
    
    .Select-option {
        padding: 10px 12px;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.15s ease;
        
        &.is-focused {
            background: #f0f4f8;
            color: ${color_text_light};
        }
        
        &.is-selected {
            background: ${color_colors_ocean};
            color: white;
        }
    }
    
    .Select-arrow-zone {
        padding-right: 8px;
    }
    
    .Select-arrow {
        border-color: ${color_text_light} transparent transparent;
        border-width: 5px 4px 2.5px;
    }
    
    &.is-open .Select-arrow {
        border-color: transparent transparent ${color_text_light};
        border-width: 0 4px 5px;
    }
`;

const modernStatusSelectStyle = css`
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    margin-bottom: 0;
    
    /* Override label container height */
    .css-og1717-labelcontainer {
        height: 0;
        display: none;
    }
    
    .Select-control {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        min-height: 44px;
        background: white;
        transition: all 0.2s ease;
        
        &:hover {
            border-color: ${color_colors_ocean};
        }
    }
    
    &.is-focused:not(.is-open) .Select-control {
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
    }
    
    .Select-placeholder,
    .Select-value {
        padding: 11px 12px;
        font-size: 14px;
        line-height: 1.4;
        display: flex;
        align-items: center;
    }
    
    .Select-value-label {
        color: ${color_text_light};
    }
    
    .Select-input {
        padding: 11px 12px;
        height: 44px;
    }
    
    .Select-input > input {
        padding: 0 0 0 2px;
        line-height: 1.4;
        font-size: 14px;
        color: ${color_text_light};
    }
    
    .Select-menu-outer {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        margin-top: 4px;
    }
    
    .Select-menu {
        max-height: 200px;
        border-radius: 6px;
    }
    
    .Select-option {
        padding: 8px 12px;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.15s ease;
        
        &.is-focused {
            background: #f0f4f8;
            color: ${color_text_light};
        }
        
        &.is-selected {
            background: ${color_colors_ocean};
            color: white;
        }
    }
    
    .Select-arrow-zone {
        padding-right: 8px;
    }
    
    .Select-arrow {
        border-color: ${color_text_light} transparent transparent;
        border-width: 5px 4px 2.5px;
    }
    
    &.is-open .Select-arrow {
        border-color: transparent transparent ${color_text_light};
        border-width: 0 4px 5px;
    }
`;

const TimeRangeField: React.FC<TimeRangeFieldProps> = (props) => {
    // Parse the value from backend format or JSON string
    const parseValue = (val: string | undefined): DaySchedule[] => {
        if (!val) {
            // No value yet — all days are unset
            return DAYS_OF_WEEK.map(day => ({
                day,
                status: 'unset' as DayStatus,
                isOpen: false,
                timeRanges: []
            }));
        }

        try {
            const parsed = JSON.parse(val);

            // Check if it's backend format (array of 7 strings with OPEN hours)
            if (Array.isArray(parsed) && parsed.length === 7 && typeof parsed[0] === 'string') {
                return DAYS_OF_WEEK.map((day, index) => {
                    const dayString = parsed[index] as string;

                    // Explicit closed sentinel
                    if (dayString === 'closed' || dayString === '00:00,00:01') {
                        return { day, status: 'closed' as DayStatus, isOpen: false, timeRanges: [] };
                    }

                    // Empty string means the day was never configured (unset)
                    if (!dayString || dayString.trim() === '') {
                        return { day, status: 'unset' as DayStatus, isOpen: false, timeRanges: [] };
                    }

                    // Parse open periods from backend
                    const openTimeRanges: TimeRange[] = [];
                    const ranges = dayString.split('|');

                    for (const range of ranges) {
                        const [start, end] = range.split(',');
                        if (start && end) {
                            openTimeRanges.push({
                                id: `time_${Date.now()}_${openTimeRanges.length}`,
                                startTime: convert24HourTo12Hour(start.trim()),
                                endTime: convert24HourTo12Hour(end.trim())
                            });
                        }
                    }

                    const status: DayStatus = openTimeRanges.length > 0 ? 'open' : 'unset';
                    return {
                        day,
                        status,
                        isOpen: status === 'open',
                        timeRanges: openTimeRanges
                    };
                });
            }

            // Otherwise, assume it's the frontend format (array of DaySchedule objects)
            return DAYS_OF_WEEK.map(day => {
                const existing = parsed.find((d: DaySchedule) => d.day === day);
                if (existing) {
                    // Migrate legacy objects that may not have a status field
                    const status: DayStatus = existing.status ?? (existing.isOpen ? 'open' : 'unset');
                    return { ...existing, status };
                }
                return { day, status: 'unset' as DayStatus, isOpen: false, timeRanges: [] };
            });
        } catch {
            return DAYS_OF_WEEK.map(day => ({
                day,
                status: 'unset' as DayStatus,
                isOpen: false,
                timeRanges: []
            }));
        }
    };

    const [schedule, setSchedule] = useState<DaySchedule[]>(parseValue(props.value));
    const [toast, setToast] = useState<ToastState | null>(null);
    const [pendingBulkAction, setPendingBulkAction] = useState<PendingBulkAction | null>(null);
    const [selectedCopyTargets, setSelectedCopyTargets] = useState<Record<string, string[]>>({});

    // Ref to prevent infinite loops from internal updates
    const internalUpdateRef = useRef<boolean>(false);
    const toastTimerRef = useRef<number | null>(null);

    // Sync schedule with external prop changes
    useEffect(() => {
        // Ignore updates originating from our own onChange
        if (internalUpdateRef.current) {
            internalUpdateRef.current = false;
            return;
        }

        const newSchedule = parseValue(props.value);
        setSchedule(newSchedule);
    }, [props.value]);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                window.clearTimeout(toastTimerRef.current);
            }
        };
    }, []);

    const showToast = (message: string, type: 'success' | 'warning' = 'success') => {
        setToast({ message, type });
        if (toastTimerRef.current) {
            window.clearTimeout(toastTimerRef.current);
        }
        toastTimerRef.current = window.setTimeout(() => setToast(null), 3000);
    };

    const scheduleToDayMap = (currentSchedule: DaySchedule[]) => {
        return currentSchedule.reduce((acc, day) => {
            acc[day.day] = day;
            return acc;
        }, {} as Record<string, DaySchedule>);
    };

    const cloneTimeRanges = (timeRanges: TimeRange[]): TimeRange[] => {
        return timeRanges.map(range => ({
            ...range,
            id: `time_${Date.now()}_${Math.floor(Math.random() * 1e6)}`
        }));
    };

    const formatTimeRanges = (timeRanges: TimeRange[]): string => {
        return timeRanges.map(range => `${range.startTime} - ${range.endTime}`).join(', ');
    };

    const formatDayList = (days: string[]): string => {
        if (days.length === 0) return '';
        if (days.length === 1) return getDayShortLabel(days[0]);
        if (days.length === 2) return `${getDayShortLabel(days[0])} and ${getDayShortLabel(days[1])}`;
        return `${days.slice(0, -1).map(getDayShortLabel).join(', ')}, and ${getDayShortLabel(days[days.length - 1])}`;
    };

    const isDayOpenWithHours = (day: DaySchedule) => day.isOpen && day.timeRanges.length > 0;

    const updateSchedule = (nextSchedule: DaySchedule[]) => {
        setSchedule(nextSchedule);
        if (props.onChange) {
            internalUpdateRef.current = true;
            props.onChange(JSON.stringify(convertToBackendFormat(nextSchedule)));
        }
    };

    const applyHoursToDays = (sourceDay: string, targetDays: string[]) => {
        const dayMap = scheduleToDayMap(schedule);
        const source = dayMap[sourceDay];
        if (!source) return;

        const nextSchedule = schedule.map(day => {
            if (day.day === sourceDay) {
                return day;
            }

            if (!targetDays.includes(day.day)) {
                return day;
            }

        return {
            ...day,
            status: source.status,
            isOpen: source.isOpen,
            timeRanges: source.isOpen ? cloneTimeRanges(source.timeRanges) : []
        };
        });

        updateSchedule(nextSchedule);
    };

    const handleSelectCopyTarget = (sourceDay: string, targetDay: string) => {
        setSelectedCopyTargets(prev => {
            const current = prev[sourceDay] ?? [];
            const next = current.includes(targetDay)
                ? current.filter(day => day !== targetDay)
                : [...current, targetDay];
            return { ...prev, [sourceDay]: next };
        });
    };

    const handleApplyCopyTargets = (sourceDay: string) => {
        const targets = selectedCopyTargets[sourceDay] ?? [];
        if (targets.length === 0) return;

        const dayMap = scheduleToDayMap(schedule);
        const conflicts = targets.filter(day => isDayOpenWithHours(dayMap[day]));

        if (conflicts.length > 0) {
            setPendingBulkAction({
                kind: 'copy',
                title: 'Replace Existing Hours?',
                sourceDay,
                targetDays: targets,
                message: `The following days already have hours: ${formatDayList(conflicts)}. Replace them with ${sourceDay}'s hours (${formatTimeRanges(dayMap[sourceDay]?.timeRanges ?? [])})?`
            });
            return;
        }

        applyHoursToDays(sourceDay, targets);
        setSelectedCopyTargets(prev => ({ ...prev, [sourceDay]: [] }));
        showToast(`Copied ${sourceDay}'s hours to ${formatDayList(targets)}.`, 'success');
    };

    const handleQuickPreset = (preset: 'all-days' | 'weekdays' | 'weekends-closed') => {
        const dayMap = scheduleToDayMap(schedule);

        if (preset === 'weekends-closed') {
            const conflicts = WEEKEND_DAYS.filter(day => isDayOpenWithHours(dayMap[day]));
            if (conflicts.length === 0) {
                showToast('Weekends are already closed.', 'success');
                return;
            }

            setPendingBulkAction({
                kind: 'close-weekends',
                title: 'Close Weekend Hours?',
                targetDays: WEEKEND_DAYS,
                message: `The following days have hours set and will be closed: ${formatDayList(conflicts)}. Continue?`
            });
            return;
        }

        const targetDays = preset === 'all-days' ? DAYS_OF_WEEK : WEEKDAY_DAYS;
        const sourceDay = targetDays.find(day => isDayOpenWithHours(dayMap[day])) ?? targetDays[0];
        const source = dayMap[sourceDay];

        if (!source || !source.isOpen || source.timeRanges.length === 0) {
            showToast('Set at least one day to Open before using a quick template.', 'warning');
            return;
        }

        const conflicts = targetDays.filter(day => day !== sourceDay && isDayOpenWithHours(dayMap[day]));
        if (conflicts.length > 0) {
            setPendingBulkAction({
                kind: 'copy',
                title: 'Replace Existing Hours?',
                sourceDay,
                targetDays: targetDays.filter(day => day !== sourceDay),
                message: `The following days already have hours: ${formatDayList(conflicts)}. Replace them with ${sourceDay}'s hours (${formatTimeRanges(source.timeRanges)})?`
            });
            return;
        }

        applyHoursToDays(sourceDay, targetDays.filter(day => day !== sourceDay));
        showToast(`${preset === 'all-days' ? 'Applied' : 'Copied'} ${sourceDay}'s hours to ${preset === 'all-days' ? 'all days' : 'weekdays'}.`, 'success');
    };

    const handleConfirmBulkAction = () => {
        if (!pendingBulkAction) return;

        if (pendingBulkAction.kind === 'copy' && pendingBulkAction.sourceDay) {
            applyHoursToDays(pendingBulkAction.sourceDay, pendingBulkAction.targetDays);
            setSelectedCopyTargets(prev => ({ ...prev, [pendingBulkAction.sourceDay!]: [] }));
            showToast(`Copied ${pendingBulkAction.sourceDay}'s hours to ${formatDayList(pendingBulkAction.targetDays)}.`, 'success');
        }

        if (pendingBulkAction.kind === 'close-weekends') {
            const nextSchedule = schedule.map(day =>
                pendingBulkAction.targetDays.includes(day.day)
                    ? { ...day, status: 'closed' as DayStatus, isOpen: false, timeRanges: [] }
                    : day
            );
            updateSchedule(nextSchedule);
            showToast('Weekend hours closed.', 'success');
        }

        setPendingBulkAction(null);
    };

    const handleCancelBulkAction = () => setPendingBulkAction(null);

    // Generate time options in 30-minute increments
    const generateTimeOptions = () => {
        const times: { value: string; label: string }[] = [];
        const periods = ['AM', 'PM'];

        for (const period of periods) {
            for (let hour = 12; hour >= 1; hour--) {
                for (const minute of ['00', '30']) {
                    const value = `${hour}:${minute} ${period}`;
                    times.push({ value, label: value });
                }
            }
        }

        // Sort times properly
        return times.sort((a, b) => {
            const parseTime = (timeStr: string) => {
                const [time, period] = timeStr.split(' ');
                const [hour, minute] = time.split(':').map(Number);
                let hours24 = hour === 12 ? 0 : hour;
                if (period === 'PM' && hour !== 12) hours24 += 12;
                if (period === 'AM' && hour === 12) hours24 = 0;
                return hours24 * 60 + minute;
            };
            return parseTime(a.value) - parseTime(b.value);
        });
    };

    const timeOptions = generateTimeOptions();

    // Calculate closed hours from open hours
    const calculateClosedHours = (openTimeRanges: TimeRange[]): string => {
        if (openTimeRanges.length === 0) {
            return 'Closed All Day';
        }

        const closedPeriods: string[] = [];
        const sortedRanges = [...openTimeRanges].sort((a, b) => {
            const aStart = convert12HourTo24Hour(a.startTime);
            const bStart = convert12HourTo24Hour(b.startTime);
            return aStart.localeCompare(bStart);
        });

        // Check if closed from midnight to first opening
        const firstStart = convert12HourTo24Hour(sortedRanges[0].startTime);
        if (firstStart !== '00:00') {
            closedPeriods.push(`${convert24HourTo12Hour('00:00')}-${convert24HourTo12Hour(firstStart)}`);
        }

        // Check gaps between open periods
        for (let i = 0; i < sortedRanges.length - 1; i++) {
            const currentEnd = convert12HourTo24Hour(sortedRanges[i].endTime);
            const nextStart = convert12HourTo24Hour(sortedRanges[i + 1].startTime);
            if (currentEnd !== nextStart) {
                closedPeriods.push(`${convert24HourTo12Hour(currentEnd)}-${convert24HourTo12Hour(nextStart)}`);
            }
        }

        // Check if closed from last closing to end of day
        const lastEnd = convert12HourTo24Hour(sortedRanges[sortedRanges.length - 1].endTime);
        // End of day is 23:59, but if they close at anything before that, show the closed period
        if (lastEnd < '23:59') {
            closedPeriods.push(`${convert24HourTo12Hour(lastEnd)}-${convert24HourTo12Hour('23:59')}`);
        }

        return closedPeriods.length > 0 ? `Closed: ${closedPeriods.join(', ')}` : 'Open 24 Hours';
    };

    // Convert schedule to backend format: Array of 7 strings with OPEN hours
    const convertToBackendFormat = (schedule: DaySchedule[]): string[] => {
        return DAYS_OF_WEEK.map(dayName => {
            const daySchedule = schedule.find(d => d.day === dayName);

            if (!daySchedule) {
                return ''; // unset
            }

            if (daySchedule.status === 'closed') {
                // Explicitly closed day uses the sentinel value
                return '00:00,00:01';
            }

            if (!daySchedule.isOpen || daySchedule.timeRanges.length === 0) {
                // Unset / open with no time ranges — treat as empty (unset)
                return '';
            }

            // Sort time ranges by start time
            const sortedRanges = [...daySchedule.timeRanges].sort((a, b) => {
                const aStart = convert12HourTo24Hour(a.startTime);
                const bStart = convert12HourTo24Hour(b.startTime);
                return aStart.localeCompare(bStart);
            });

            // Convert each open period to 24-hour format
            const openPeriods = sortedRanges.map(range => {
                const start = convert12HourTo24Hour(range.startTime);
                const end = convert12HourTo24Hour(range.endTime);
                return `${start},${end}`;
            });

            return openPeriods.join('|');
        });
    };

    const updateValue = (newSchedule: DaySchedule[]) => {
        setSchedule(newSchedule);
        if (props.onChange) {
            // Set ref before calling onChange to prevent useEffect from reacting
            internalUpdateRef.current = true;
            // Convert to backend format (array of 7 strings)
            const backendFormat = convertToBackendFormat(newSchedule);
            props.onChange(JSON.stringify(backendFormat));
        }
    };

    // Validate that open days have at least one time range
    const getValidationError = () => {
        for (const day of schedule) {
            // Check if open day has time ranges
            if (day.isOpen && day.timeRanges.length === 0) {
                return `${day.day}: Please add hours or mark as closed.`;
            }

            // Check for overlapping time ranges
            if (day.isOpen && day.timeRanges.length > 1) {
                const sortedRanges = [...day.timeRanges].sort((a, b) => {
                    const aStart = convert12HourTo24Hour(a.startTime);
                    const bStart = convert12HourTo24Hour(b.startTime);
                    return aStart.localeCompare(bStart);
                });

                for (let i = 0; i < sortedRanges.length - 1; i++) {
                    const currentEnd = convert12HourTo24Hour(sortedRanges[i].endTime);
                    const nextStart = convert12HourTo24Hour(sortedRanges[i + 1].startTime);

                    if (currentEnd > nextStart) {
                        return `${day.day}: Time ranges cannot overlap.`;
                    }
                }
            }

            // Check that end time is after start time
            for (const range of day.timeRanges) {
                const start = convert12HourTo24Hour(range.startTime);
                const end = convert12HourTo24Hour(range.endTime);
                if (start >= end) {
                    return `${day.day}: End time must be after start time.`;
                }
            }
        }

        return null;
    };

    const validationError = getValidationError();

    const handleToggleOpen = (day: string, selectedValue: string | undefined) => {
        if (selectedValue === 'Open') {
            updateValue(schedule.map(d =>
                d.day === day ? {
                    ...d,
                    status: 'open' as DayStatus,
                    isOpen: true,
                    timeRanges: d.timeRanges.length === 0
                        ? [{ id: `time_${Date.now()}_${Math.floor(Math.random() * 1e6)}`, startTime: '9:00 AM', endTime: '5:00 PM' }]
                        : d.timeRanges
                } : d
            ));
        } else if (selectedValue === 'Closed') {
            updateValue(schedule.map(d =>
                d.day === day ? { ...d, status: 'closed' as DayStatus, isOpen: false, timeRanges: [] } : d
            ));
        }
    };

    const handleAddTimeRange = (day: string) => {
        const newTimeRange: TimeRange = {
            id: `time_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
            startTime: '9:00 AM',
            endTime: '5:00 PM'
        };
        updateValue(schedule.map(d =>
            d.day === day ? { ...d, timeRanges: [...d.timeRanges, newTimeRange] } : d
        ));
    };

    const handleRemoveTimeRange = (day: string, timeRangeId: string) => {
        updateValue(schedule.map(d =>
            d.day === day ? { ...d, timeRanges: d.timeRanges.filter(tr => tr.id !== timeRangeId) } : d
        ));
    };

    const handleTimeRangeChange = (day: string, timeRangeId: string, field: 'startTime' | 'endTime', value: string) => {
        updateValue(schedule.map(d =>
            d.day === day ? {
                ...d,
                timeRanges: d.timeRanges.map(tr =>
                    tr.id === timeRangeId ? { ...tr, [field]: value } : tr
                )
            } : d
        ));
    };

    return (
        <>
            <div className={`${containerStyle} ${props.className || ''}`}>
                <div className={fieldHeaderRow}>
                    {props.label && <label className={labelStyle}>{props.label}</label>}
                    {props.defaultValue !== undefined && props.value !== props.defaultValue && (
                        <button
                            className={resetButtonStyle}
                            type="button"
                            onClick={() => props.onReset ? props.onReset() : props.onChange && props.onChange(props.defaultValue)}
                        >
                            Reset to default
                        </button>
                    )}
                </div>
                {validationError && <div className={errorStyle}>{validationError}</div>}

                <div className={presetRow}>
                    <div className={presetLabel}>Quick templates</div>
                    <div className={presetButtons}>
                        <Button themes={['blue-pill']} type="button" text="Same hours every day" onClick={() => handleQuickPreset('all-days')} disabled={props.disabled} />
                        <Button themes={['blue-pill']} type="button" text="Mon-Fri only" onClick={() => handleQuickPreset('weekdays')} disabled={props.disabled} />
                        <Button themes={['blue-pill']} type="button" text="Weekends closed" onClick={() => handleQuickPreset('weekends-closed')} disabled={props.disabled} />
                    </div>
                </div>

                <div className={daysContainer}>
                    {schedule.map((daySchedule) => {
                        const selectedTargetDays = selectedCopyTargets[daySchedule.day] ?? [];
                        return (
                            <div key={daySchedule.day} className={dayCard}>
                                <div className={dayHeader}>
                                    <span className={dayLabel}>{daySchedule.day.toUpperCase()}</span>
                                    <div className={statusSelectWrapper}>
                                        <SelectField
                                            name={`${props.name}_${daySchedule.day}_status`}
                                            placeholder="Select hours"
                                            value={daySchedule.status === 'open' ? 'Open' : daySchedule.status === 'closed' ? 'Closed' : undefined}
                                            onChange={(selected: any) => handleToggleOpen(daySchedule.day, selected?.value)}
                                            options={[
                                                { value: 'Open', label: 'Open' },
                                                { value: 'Closed', label: 'Closed' }
                                            ]}
                                            disabled={props.disabled}
                                            clearable={false}
                                            useForm={false}
                                            customClassName={modernStatusSelectStyle}
                                            hideLabel={true}
                                        />
                                        {daySchedule.isOpen && daySchedule.timeRanges.length > 0 && (
                                            <div className={closedHoursInfo}>
                                                {calculateClosedHours(daySchedule.timeRanges)}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {daySchedule.isOpen && (
                                    <div className={timeRangesContainer}>
                                        <div className={openHoursLabel}>Open Hours:</div>
                                        {daySchedule.timeRanges.map((timeRange) => (
                                            <div key={timeRange.id} className={timeRangeRow}>
                                                <div className={timeRangeFields}>
                                                    <SelectField
                                                        name={`${props.name}_${daySchedule.day}_start_${timeRange.id}`}
                                                        placeholder="Start time"
                                                        value={timeRange.startTime}
                                                        onChange={(selected: any) =>
                                                            handleTimeRangeChange(daySchedule.day, timeRange.id, 'startTime', selected?.value || '')
                                                        }
                                                        options={timeOptions}
                                                        disabled={props.disabled}
                                                        clearable={false}
                                                        useForm={false}
                                                        customClassName={modernSelectStyle}
                                                        hideLabel={true}
                                                    />
                                                    <span className={timeSeparator}>—</span>
                                                    <SelectField
                                                        name={`${props.name}_${daySchedule.day}_end_${timeRange.id}`}
                                                        placeholder="End time"
                                                        value={timeRange.endTime}
                                                        onChange={(selected: any) =>
                                                            handleTimeRangeChange(daySchedule.day, timeRange.id, 'endTime', selected?.value || '')
                                                        }
                                                        options={timeOptions}
                                                        disabled={props.disabled}
                                                        clearable={false}
                                                        useForm={false}
                                                        customClassName={modernSelectStyle}
                                                        hideLabel={true}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    className={deleteButton}
                                                    onClick={() => handleRemoveTimeRange(daySchedule.day, timeRange.id)}
                                                    disabled={props.disabled}
                                                    title="Remove time range"
                                                >
                                                    <img src={deleteIcon} alt="Remove" />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            className={addTimeRangeButton}
                                            onClick={() => handleAddTimeRange(daySchedule.day)}
                                            disabled={props.disabled}
                                        >
                                            <img src={addIcon} alt="Add" />
                                            Add time range
                                        </button>

                                        <div className={copySection}>
                                            <div className={copyTitle}>Copy these hours to:</div>
                                            <div className={copyTargetsStyle}>
                                                {DAYS_OF_WEEK.filter(day => day !== daySchedule.day).map(day => (
                                                    <label key={day} className={copyTargetItem}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedTargetDays.includes(day)}
                                                            onChange={() => handleSelectCopyTarget(daySchedule.day, day)}
                                                            disabled={props.disabled}
                                                        />
                                                        <span>{getDayShortLabel(day)}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            <Button
                                                themes={['primary-small']}
                                                type="button"
                                                text="Apply to selected days"
                                                disabled={props.disabled || selectedTargetDays.length === 0}
                                                onClick={() => handleApplyCopyTargets(daySchedule.day)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {toast && (
                <div className={`${toastStyle} ${toast.type === 'success' ? successToastStyle : warningToastStyle}`}>
                    {toast.message}
                </div>
            )}

            {pendingBulkAction && (
                <ConfirmationDialog
                    title={pendingBulkAction.title}
                    deleteText={pendingBulkAction.kind === 'close-weekends' ? 'Close days' : 'Replace hours'}
                    warning={true}
                    onClose={handleCancelBulkAction}
                    onConfirm={handleConfirmBulkAction}
                >
                    <div className={modalBodyStyle}>
                        <p>{pendingBulkAction.message}</p>
                    </div>
                </ConfirmationDialog>
            )}
        </>
    );
};

const containerStyle = css`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const fieldHeaderRow = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;
`;

const resetButtonStyle = css`
    background: none;
    border: none;
    color: ${color_colors_ocean};
    font-family: Muli;
    font-size: 12px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    text-decoration: none;
    margin-left: auto;

    &:hover {
        background-color: ${color_variants_ocean_light_1};
    }
`;

const labelStyle = css`
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;
    color: ${color_text_light};
`;

const errorStyle = css`
    font-size: 14px;
    color: #d32f2f;
    margin-bottom: 12px;
    padding: 8px 12px;
    background-color: #ffebee;
    border-radius: 4px;
    border-left: 4px solid #d32f2f;
`;

const daysContainer = css`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const presetRow = css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
`;

const presetLabel = css`
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: ${color_text_light};
`;

const presetButtons = css`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 0 -16px;
    button {
        margin: 0;
        height: 34px;
        padding: 0 14px;
        font-size: 12px;
    }
`;

const dayCard = css`
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    padding: 32px;
    background: white;
`;

const dayHeader = css`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 0;
    padding-left: 4px;
`;

const dayLabel = css`
    font-size: 14px;
    font-weight: 600;
    min-width: 100px;
    color: ${color_text_light};
`;

const statusSelectWrapper = css`
    min-width: 140px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const closedHoursInfo = css`
    font-size: 12px;
    color: ${color_text_light};
    padding: 4px 8px;
    background: #f8f9fa;
    border-radius: 4px;
    font-style: italic;
`;

const timeRangesContainer = css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-left: 116px;
    margin-top: 12px;
`;

const copySection = css`
    border-top: 1px solid ${color_shades_dark};
    margin-top: 12px;
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const copyTitle = css`
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_light};
`;

const copyTargetsStyle = css`
    display: flex;
    flex-wrap: wrap;
    gap: 10px 16px;
`;

const copyTargetItem = css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${color_text_light};
    user-select: none;
    input {
        margin: 0;
    }
`;

const toastStyle = css`
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 2000;
    max-width: 360px;
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
    color: white;
    font-size: 14px;
    line-height: 1.4;
`;

const successToastStyle = css`
    background: #2f855a;
`;

const warningToastStyle = css`
    background: #b7791f;
`;

const modalBodyStyle = css`
    padding: 24px 32px 0;
    font-size: 14px;
    color: ${color_text_light};
    p {
        margin: 0;
    }
`;

const openHoursLabel = css`
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_light};
    margin-bottom: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const timeRangeRow = css`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const timeRangeFields = css`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
`;

const timeSeparator = css`
    font-size: 16px;
    color: ${color_text_light};
    flex-shrink: 0;
`;

const deleteButton = css`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.5;
    transition: opacity 0.2s;
    
    &:hover {
        opacity: 1;
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.3;
    }
    
    img {
        width: 18px;
        height: 18px;
    }
`;

const addTimeRangeButton = css`
    background: transparent;
    border: 1px dashed ${color_shades_dark};
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    color: ${color_colors_ocean};
    transition: all 0.2s;
    
    &:hover {
        background: #f9f9f9;
        border-color: ${color_colors_ocean};
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
    
    img {
        width: 16px;
        height: 16px;
    }
`;

export default TimeRangeField;
